import { adNetworkMetrics } from '@/mocks/adNetwork';

const howItWorks = [
  {
    step: '01',
    icon: 'ri-plug-line',
    title: 'Activá la red',
    description: 'Con un solo click en tu panel de administración, tu radio se suma a la Red Publicitaria RFW. Sin configuración técnica, sin contratos largos.',
  },
  {
    step: '02',
    icon: 'ri-speaker-line',
    title: 'Emití tu programación',
    description: 'Seguís haciendo radio como siempre. RFW inserta anuncios de forma inteligente en los momentos justos, sin interrumpir tu contenido.',
  },
  {
    step: '03',
    icon: 'ri-money-dollar-circle-line',
    title: 'Recibí tus ganancias',
    description: 'Cada mes cobrás tu parte. Métricas claras, pagos puntuales. Cuanto más crece tu audiencia, más ganás.',
  },
];

export default function RedPublicitariaSection() {
  const r = adNetworkMetrics;

  return (
    <section id="red-publicitaria" className="relative py-20 md:py-28 overflow-hidden bg-background-100">
      {/* Ambient glow top right */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-amber-500/4 rounded-full blur-3xl pointer-events-none"></div>

      {/* Dot grid */}
      <div className="absolute inset-0 pointer-events-none opacity-[0.02]" style={{
        backgroundImage: 'radial-gradient(circle, currentColor 1px, transparent 1px)',
        backgroundSize: '20px 20px',
      }}></div>

      <div className="relative z-10 w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        {/* ── Header ── */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-xs font-semibold mb-5">
            <i className="ri-global-line text-xs"></i>
            Red Publicitaria RFW
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 tracking-tight leading-[1.1]">
            Tu radio gana plata{' '}
            <span className="text-amber-600">mostrando anuncios</span>
          </h2>
          <p className="mt-4 text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Activá la Red Publicitaria RFW y empezá a generar ingresos pasivos con pauta programática de marcas nacionales e internacionales. Sin esfuerzo extra, sin vender nada.
          </p>
        </div>

        {/* ── Main two-column layout ── */}
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 mb-14">
          {/* LEFT: How it works */}
          <div className="flex-1 space-y-5">
            <p className="text-foreground-400 text-xs font-semibold uppercase tracking-wider mb-3">Cómo funciona</p>
            {howItWorks.map((item, idx) => (
              <div
                key={item.step}
                className="flex gap-4 p-5 rounded-2xl bg-background-50 border border-background-200/70 transition-all duration-300 hover:border-amber-500/30 hover:bg-amber-50/30"
              >
                <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-xl bg-amber-500/10 text-amber-600">
                  <i className={`${item.icon} text-lg`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-amber-500 font-extrabold text-sm tabular-nums">{item.step}</span>
                    <h3 className="text-foreground-950 font-bold text-sm">{item.title}</h3>
                  </div>
                  <p className="text-foreground-500 text-xs md:text-sm leading-relaxed">{item.description}</p>
                </div>
                {idx < howItWorks.length - 1 && (
                  <div className="hidden md:flex items-center">
                    <i className="ri-arrow-down-line text-foreground-300 text-lg"></i>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* RIGHT: Revenue split + metrics */}
          <div className="flex-1 flex flex-col gap-5">
            <p className="text-foreground-400 text-xs font-semibold uppercase tracking-wider mb-0">Revenue Share</p>

            {/* Split bar */}
            <div className="rounded-2xl border border-background-200/70 bg-background-50 p-6 md:p-7">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <span className="text-foreground-950 font-bold text-sm">Radio</span>
                  <span className="text-amber-600 font-extrabold text-2xl tabular-nums">60%</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-foreground-600 font-extrabold text-2xl tabular-nums">40%</span>
                  <span className="text-foreground-950 font-bold text-sm">RFW</span>
                  <div className="w-3 h-3 rounded-full bg-foreground-400"></div>
                </div>
              </div>

              {/* Visual bar */}
              <div className="w-full h-4 rounded-full bg-foreground-200/50 overflow-hidden flex">
                <div className="h-full bg-amber-500 rounded-l-full flex items-center justify-center" style={{ width: '60%' }}>
                  <span className="text-white text-[9px] font-bold">60%</span>
                </div>
                <div className="h-full bg-foreground-400 rounded-r-full flex items-center justify-center" style={{ width: '40%' }}>
                  <span className="text-white text-[9px] font-bold">40%</span>
                </div>
              </div>

              <p className="mt-3 text-foreground-400 text-[11px] leading-relaxed text-center">
                La radio se lleva la mayor parte. Simple, transparente, sin letra chica.
              </p>
            </div>

            {/* Metrics grid */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-background-200/70 bg-background-50 p-4 text-center">
                <div className="text-amber-600 font-extrabold text-xl md:text-2xl tabular-nums leading-tight">
                  {r.participatingRadios}
                </div>
                <div className="text-foreground-400 text-[10px] font-medium">radios monetizando</div>
              </div>
              <div className="rounded-2xl border border-background-200/70 bg-background-50 p-4 text-center">
                <div className="text-amber-600 font-extrabold text-xl md:text-2xl tabular-nums leading-tight">
                  {(r.totalImpressions / 1000000).toFixed(1)}M
                </div>
                <div className="text-foreground-400 text-[10px] font-medium">impresiones totales</div>
              </div>
              <div className="rounded-2xl border border-background-200/70 bg-background-50 p-4 text-center">
                <div className="text-amber-600 font-extrabold text-xl md:text-2xl tabular-nums leading-tight">
                  {r.avgCtr}%
                </div>
                <div className="text-foreground-400 text-[10px] font-medium">CTR promedio</div>
              </div>
              <div className="rounded-2xl border border-background-200/70 bg-background-50 p-4 text-center">
                <div className="text-emerald-600 font-extrabold text-xl md:text-2xl tabular-nums leading-tight">
                  ${(r.totalRadioRevenue / 1000).toFixed(0)}K
                </div>
                <div className="text-foreground-400 text-[10px] font-medium">ganancias totales radios</div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Bottom highlight strip ── */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 md:gap-10 text-center p-6 rounded-2xl bg-amber-50/60 border border-amber-200/60">
          <div className="flex items-center gap-2 text-foreground-700 text-xs font-medium">
            <i className="ri-flashlight-line text-amber-500 text-base"></i>
            <span>Activación <strong className="text-foreground-950">inmediata</strong> desde tu panel</span>
          </div>
          <div className="hidden sm:block w-px h-5 bg-amber-200"></div>
          <div className="flex items-center gap-2 text-foreground-700 text-xs font-medium">
            <i className="ri-shield-check-line text-amber-500 text-base"></i>
            <span>Anuncios <strong className="text-foreground-950">curados</strong> y relevantes para tu audiencia</span>
          </div>
          <div className="hidden sm:block w-px h-5 bg-amber-200"></div>
          <div className="flex items-center gap-2 text-foreground-700 text-xs font-medium">
            <i className="ri-calendar-check-line text-amber-500 text-base"></i>
            <span>Pagos <strong className="text-foreground-950">mensuales</strong> sin mínimo</span>
          </div>
        </div>
      </div>
    </section>
  );
}